import ScratchJr from '../../../ScratchJr';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import Palette from '../../../ui/Palette';
import Thumbs from '../../../ui/Thumbs';
import {gn} from '../../../../utils/lib';
import {evaluateSuccessCriteria} from '../../../../../../web/ai/activity-success';

const DEFAULT_PHASE = 'INTRO';
const PHASES = Object.freeze({
    INTRO: 'INTRO',
    EXPLORE_STARTER: 'EXPLORE_STARTER',
    READ_CODE: 'READ_CODE',
    TASK_TAP: 'TASK_TAP',
    PHASE_2_UNLOCK: 'PHASE_2_UNLOCK',
    TASK_BUMP: 'TASK_BUMP',
    TASK_PAGE: 'TASK_PAGE',
    PHASE_3_UNLOCK: 'PHASE_3_UNLOCK',
    BUILD_MAP: 'BUILD_MAP',
    CELEBRATION: 'CELEBRATION',
    BONUS_PLAY: 'BONUS_PLAY',
    COMPLETE: 'COMPLETE'
});

const PHASE_RANKS = Object.freeze({
    INTRO: 0,
    EXPLORE_STARTER: 1,
    READ_CODE: 2,
    TASK_TAP: 3,
    PHASE_2_UNLOCK: 4,
    TASK_BUMP: 5,
    TASK_PAGE: 6,
    PHASE_3_UNLOCK: 7,
    BUILD_MAP: 8,
    CELEBRATION: 9,
    BONUS_PLAY: 10,
    COMPLETE: 11
});

const DEFAULT_VOICE_OVER = Object.freeze({
    'VO-01': "Welcome, mapmaker! Someone started building a treasure map game, but they didn't finish it. Can you help? First, let's see what's already here. Tap the green flag!",
    'VO-02': "Explorer walks and talks! Dragon paces around! But the chest doesn't do anything, and the key just sits there. They need YOUR code!",
    'VO-03': "See? Explorer already has blocks. The green flag starts it. Move right, then say something!",
    'VO-04': "The chest doesn't have any blocks yet. It's waiting for YOU to decide what it does!",
    'VO-05': "Dragon has blocks already! The red forever block makes Dragon pace back and forth forever.",
    'VO-06': "What should happen when the player taps the chest?",
    'VO-07': "The chest opens when you tap it! The player controls what happens!",
    'VO-08': "New blocks! The bump block makes things happen when characters collide. And the page block can take the player somewhere new!",
    'VO-09': "When Explorer bumps the dragon! Good surprise for the player!",
    'VO-10': "A secret treasure room! The player is going to love this!",
    'VO-11': "Message blocks! One character sends a signal, and another one listens. Try making the key tell the chest to open!",
    'VO-12': "The key told the chest to open, without even touching it! That's the power of messages!",
    'VO-13': "What's in the treasure room? Add some characters and give them scripts!",
    'VO-14': "THE TREASURE MAP IS COMPLETE! You built a game someone else can play! Taps, bumps, messages. Different things trigger different code!",
    'VO-15': "Now play your own treasure map from the beginning! Pretend you've never seen it before.",
    'VO-16': "You started with someone else's work and made it YOURS! When different things happen, different code runs. And you made it for someone else to play!",
    'VO-17': "Have you tried tapping the flag yet? It's fun to see what's already there!",
    'VO-18': "Explorer ran into Dragon, but nothing happened! Want to make something happen when they bump?",
    'VO-19': "Explorer is empty now! Add some blocks to make Explorer do something.",
    'VO-20': "Tap on the chest and look at its blocks. Then add a Start on Tap block.",
    'VO-21': "The key doesn't have any blocks yet either! What should it do when the player taps it?",
    'VO-22': "Want to tell a story? Use the purple say block to make characters talk! What would Explorer say to the player?"
});

const DEFAULT_AVAILABLE_BLOCKS = Object.freeze({
    INTRO: ['onflag'],
    EXPLORE_STARTER: ['onflag', 'onclick', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack'],
    READ_CODE: ['onflag', 'onclick', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack'],
    TASK_TAP: ['onflag', 'onclick', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack'],
    PHASE_2_UNLOCK: ['onflag', 'onclick', 'ontouch', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'gotopage'],
    TASK_BUMP: ['onflag', 'onclick', 'ontouch', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'gotopage'],
    TASK_PAGE: ['onflag', 'onclick', 'ontouch', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'gotopage'],
    PHASE_3_UNLOCK: ['onflag', 'onclick', 'ontouch', 'onmessage', 'message', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'forever', 'gotopage'],
    BUILD_MAP: ['onflag', 'onclick', 'ontouch', 'onmessage', 'message', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'forever', 'gotopage'],
    CELEBRATION: ['onflag', 'onclick', 'ontouch', 'onmessage', 'message', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'forever', 'gotopage'],
    BONUS_PLAY: ['onflag', 'onclick', 'ontouch', 'onmessage', 'message', 'forward', 'back', 'up', 'down', 'hop', 'say', 'grow', 'shrink', 'show', 'hide', 'playsnd', 'wait', 'repeat', 'setspeed', 'endstack', 'forever', 'gotopage']
});

const DEFAULT_IDLE_SCAFFOLDING = Object.freeze({
    EXPLORE_STARTER: [30000, 50000, 70000, 90000],
    READ_CODE: [30000, 50000, 70000, 90000],
    TASK_TAP: [30000, 50000, 70000, 90000],
    TASK_BUMP: [30000, 50000, 70000, 90000],
    TASK_PAGE: [30000, 50000, 70000, 90000],
    BUILD_MAP: [30000, 50000, 70000, 90000],
    BONUS_PLAY: []
});

export default class TreasureMapFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._stage = container.querySelector('.stage') || container;
        this._overlay = null;
        this._celebrationLayer = null;
        this._completionOverlay = null;
        this._doneBtn = null;
        this._ghostHand = null;
        this._paletteRestrictor = null;
        this._scriptMonitor = null;
        this._persistTimer = null;
        this._idleTimers = [];
        this._phaseTimeout = null;
        this._phase = DEFAULT_PHASE;
        this._config = this._buildConfig(activityData);
        this._starterAmbienceSignatures = TreasureMapFlow._collectStarterAmbienceSignatures(activityData);

        // Interaction tracking
        this._onPointerDown = this._handleInteraction.bind(this);
        this._onPointerUpReplay = this._replayCurrentNarration.bind(this);
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);

        // State tracking
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        this._sessionStartedAt = Date.now();
        this._flagTapCount = 0;
        this._completionSaved = false;
        this._speechVoice = null;
        this._speechVoiceScore = 0;

        // Code-reading narration tracking (per-character, fires once)
        this._codeNarrationFired = {};

        // Milestone tracking
        this._starterProjectRun = false;
        this._explorerScriptViewed = false;
        this._bumpCollisionCount = 0;

        // Cache starter block-type signatures from projectTemplate (P1-7 + Claude #3)
        this._starterSignatures = this._computeStarterSignatures(activityData);

        // Outcome flags per spec section 11.1
        this._outcomeFlags = {
            outcome_1: false, // Ran starter project before modifying
            outcome_2: false, // Modified at least one starter script
            outcome_3: false, // Created Start on Tap script
            outcome_4: false, // Created Start on Bump script
            outcome_5: false, // Used Send/Receive Message pair
            outcome_6: false, // Used Go to Page block
            outcome_7: false, // Added new character or script beyond starter
            outcome_8: false  // Full playthrough completed
        };

        // Interaction types used
        this._interactionTypes = new Set();
    }

    async init () {
        this._renderChrome();
        this._paletteRestrictor = new PaletteRestrictor(this._container, []);
        this._attachListeners();
        this._initVoiceSelection();
        await this._waitForPreferredVoice();

        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        if (saved) {
            this._hydrateFromProgress(saved);
        }

        const resumePhase = this._getResumePhase(saved && saved.currentState ? saved.currentState : null);

        if (resumePhase) {
            this._phase = resumePhase;
            this._syncVisualState({skipReset: true});
            this._startScriptMonitor();
            this._startIdleScaffolding();
            this._speak('VO-06', this._buildPhasePrompt(this._phase, true));
            if (saved && resumePhase !== this._normalizeResumePhase(saved.currentState)) {
                await this._persistProgress({currentState: resumePhase});
            }
            return;
        }

        this._setPhase(PHASES.INTRO, {skipPersist: true, skipReset: true});
        this._syncVisualState({skipReset: true});
        this._startScriptMonitor();
        this._playIntroSequence();
        await this._persistProgress({
            status: 'in_progress',
            currentState: PHASES.INTRO
        });
    }

    onStateChange (prevState, newState) {
        if (prevState === 'SUCCESS' && newState === 'BUILDING') {
            return;
        }

        if (newState === 'RUNNING') {
            this._clearIdleTimers();
            return;
        }

        if (newState === 'VALIDATING') {
            this._validateRun();
            return;
        }

        if (newState === 'BUILDING' || newState === 'READY') {
            this._startIdleScaffolding();
        }
    }

    getBlockCount () {
        return this._getAllBlocksAllSprites().length;
    }

    evaluate () {
        const simState = this._buildSimState();
        const shared = evaluateSuccessCriteria(this._activityData, simState);
        return Object.assign({}, shared, {
            usedBlocks: this.getBlockCount(),
            storyPhase: this._phase,
            flagTapCount: this._flagTapCount,
            interactionTypes: Array.from(this._interactionTypes)
        });
    }

    _buildSimState () {
        return {
            phaseFlags: {
                completed: this._phase === PHASES.COMPLETE || this._completionSaved,
                storyPhase: this._phase,
                flagTapCount: this._flagTapCount,
                interactionTypes: Array.from(this._interactionTypes)
            }
        };
    }

    showHint () {
        this._replayCurrentNarration();
    }

    captureRunStartPosition () {}

    shouldResetSpritesBeforeRun () {
        return true;
    }

    shouldEvaluateWhileThreadsRunning (runtime) {
        const activeThreads = runtime && Array.isArray(runtime.threadsRunning)
            ? runtime.threadsRunning.filter((thread) => thread && thread.isRunning)
            : [];

        return activeThreads.length > 0 &&
            activeThreads.every((thread) => this._isStarterAmbienceThread(thread));
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }

        this._sessionStartedAt = Date.now();
        this._idleTimeMs = 0;
        this._idlePausedAt = null;
        this._lastInteractionAt = Date.now();
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._flagTapCount = 0;
        this._completionSaved = false;
        this._starterProjectRun = false;
        this._explorerScriptViewed = false;
        this._bumpCollisionCount = 0;
        this._codeNarrationFired = {};
        this._interactionTypes = new Set();
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false,
            outcome_6: false,
            outcome_7: false,
            outcome_8: false
        };

        this._setPhase(PHASES.EXPLORE_STARTER, {skipReset: false});
        this._startScriptMonitor();
        this._startIdleScaffolding();
        this._speak('VO-01');
        this._persistProgress({
            status: 'in_progress',
            completedAt: null
        });
        return true;
    }

    destroy () {
        this._clearIdleTimers();
        this._clearPersistTimer();
        this._clearPhaseTimeout();
        this._stopScriptMonitor();
        this._dismissCompletionModal();
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        if (window.speechSynthesis && typeof window.speechSynthesis.removeEventListener === 'function') {
            window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangeHandler);
        }
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
            this._paletteRestrictor = null;
        }
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
        }
        if (this._overlay && this._overlay.parentNode) {
            this._overlay.parentNode.removeChild(this._overlay);
        }
        this._container.removeEventListener('pointerdown', this._onPointerDown, true);
    }

    _computeStarterSignatures (activityData) {
        // Store block-type signatures (not just counts) so that swapping one
        // block type for another is detected as a modification.
        const signatures = {};
        const templatePage = activityData.projectTemplate &&
            Array.isArray(activityData.projectTemplate.pages)
            ? activityData.projectTemplate.pages[0] : null;
        const sprites = templatePage && Array.isArray(templatePage.sprites) ? templatePage.sprites : [];
        sprites.forEach((sprite) => {
            if (!sprite.role) {
                return;
            }
            const types = [];
            if (Array.isArray(sprite.scripts)) {
                sprite.scripts.forEach((script) => {
                    if (Array.isArray(script)) {
                        script.forEach((block) => {
                            if (block && block.blocktype) {
                                types.push(block.blocktype);
                            }
                        });
                    }
                });
            }
            signatures[sprite.role] = types.join(',');
        });
        return signatures;
    }

    // --- Config ---

    _buildConfig (activityData) {
        const tm = activityData.treasureMap || {};
        return {
            playerIndex: typeof tm.playerIndex === 'number' ? tm.playerIndex : 0,
            introDelayMs: tm.introDelayMs || 900,
            restartPhase: tm.restartPhase || 'EXPLORE_STARTER',
            voiceOver: {
                ...DEFAULT_VOICE_OVER,
                ...(tm.voiceOver || {})
            },
            availableBlocks: {
                ...DEFAULT_AVAILABLE_BLOCKS,
                ...(tm.availableBlocks || {})
            },
            idleScaffolding: {
                ...DEFAULT_IDLE_SCAFFOLDING,
                ...(tm.idleScaffolding || {})
            },
            codeNarration: tm.codeNarration || {},
            audienceFraming: tm.audienceFraming || {},
            voiceConfidenceThreshold: tm.voiceConfidenceThreshold || 90
        };
    }

    // --- Chrome / UI ---

    _renderChrome () {
        this._overlay = document.createElement('div');
        this._overlay.className = 'activity-treasuremap-layer';

        this._celebrationLayer = document.createElement('div');
        this._celebrationLayer.className = 'activity-treasuremap-celebration';
        this._overlay.appendChild(this._celebrationLayer);

        // ACC-13: Page number indicator for multi-page editing
        this._pageIndicator = document.createElement('div');
        this._pageIndicator.className = 'activity-treasuremap-page-indicator';
        const pageCount = this._activityData.projectTemplate &&
            Array.isArray(this._activityData.projectTemplate.pages)
            ? this._activityData.projectTemplate.pages.length : 1;
        for (let i = 0; i < pageCount; i++) {
            const dot = document.createElement('span');
            dot.className = 'activity-treasuremap-page-dot';
            dot.textContent = String(i + 1);
            if (i === 0) {
                dot.classList.add('is-current');
            }
            this._pageIndicator.appendChild(dot);
        }
        this._overlay.appendChild(this._pageIndicator);

        this._stage.appendChild(this._overlay);
    }

    _updatePageIndicator () {
        if (!this._pageIndicator) {
            return;
        }
        const pages = this._getAllPages();
        const currentPage = ScratchJr.stage && ScratchJr.stage.currentPage ? ScratchJr.stage.currentPage : null;
        const currentIndex = currentPage ? pages.indexOf(currentPage) : 0;
        Array.from(this._pageIndicator.childNodes || []).forEach((dot, i) => {
            dot.classList.toggle('is-current', i === currentIndex);
        });
    }

    // ACC-11: Add shimmer class to sprites that have onclick scripts
    _updateTappableShimmer () {
        const pages = this._getAllPages();
        pages.forEach((page) => {
            const spriteIds = page && typeof page.getSprites === 'function' ? page.getSprites() : [];
            spriteIds.forEach((spriteId) => {
                const spriteEl = spriteId ? gn(spriteId) : null;
                const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
                if (!sprite || !sprite.div) {
                    return;
                }
                const scriptsDiv = gn(`${sprite.id}_scripts`);
                const hasOnclick = scriptsDiv && scriptsDiv.owner &&
                    typeof scriptsDiv.owner.getBlocks === 'function' &&
                    (scriptsDiv.owner.getBlocks() || []).some((b) => b && b.blocktype === 'onclick');
                sprite.div.classList.toggle('activity-treasuremap-tappable', !!hasOnclick);
            });
        });
    }

    // ACC-12: Show a brief visual flash at a position on the stage
    _showBumpFlash (x, y) {
        const flash = document.createElement('div');
        flash.className = 'activity-treasuremap-bump-flash';
        flash.style.left = `${x}px`;
        flash.style.top = `${y}px`;
        this._stage.appendChild(flash);
        setTimeout(() => {
            if (flash.parentNode) {
                flash.parentNode.removeChild(flash);
            }
        }, 500);
    }

    _attachListeners () {
        this._container.addEventListener('pointerdown', this._onPointerDown, true);
    }

    _handleInteraction () {
        if (this._idlePausedAt) {
            this._idleTimeMs += Math.max(0, Date.now() - this._idlePausedAt);
            this._idlePausedAt = null;
        }
        this._lastInteractionAt = Date.now();
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._startIdleScaffolding();

        // Check for code narration — if user selects a character with pre-built scripts
        this._checkCodeNarration();

        // Script analysis is handled by the 500ms _scriptMonitor to avoid
        // running _analyzeAllScripts() on every pointer event (P0-1 fix).
    }

    // --- Phase Management ---

    _setPhase (phase, opts = {}) {
        this._clearPhaseTimeout();
        this._phase = phase;
        this._syncVisualState({skipReset: Boolean(opts.skipReset)});
        this._startIdleScaffolding();
        if (!opts.skipPersist) {
            this._persistProgress();
        }
    }

    _syncVisualState (opts = {}) {
        if (this._paletteRestrictor) {
            this._paletteRestrictor.updateAllowed(this._getAllowedBlocksForPhase());
        }

        if (!opts.skipReset) {
            ScratchJr.stopStrips();
            ScratchJr.resetSprites();
        }

        this._enableAllSprites();
        this._updatePageIndicator();
        this._updateTappableShimmer();

        if (this._phase === PHASES.BONUS_PLAY || this._phase === PHASES.COMPLETE) {
            this._showDoneButton();
        } else {
            this._hideDoneButton();
        }
    }

    _getAllowedBlocksForPhase () {
        return this._config.availableBlocks[this._phase] ||
            this._config.availableBlocks.BUILD_MAP ||
            [];
    }

    _enableAllSprites () {
        // Unlike single-sprite activities, treasure map lets child select any sprite.
        // Thumbs.updateSprites() may not create thumbnails for non-player sprites
        // unless we rebuild the rail first (Codex review: P1 blocker).
        if (Thumbs && typeof Thumbs.updateSprites === 'function') {
            try {
                Thumbs.updateSprites();
            } catch (_) {}
        }
        const rail = document.getElementById('spritecc');
        if (!rail) {
            return;
        }
        Array.from(rail.childNodes || []).forEach((thumb) => {
            thumb.style.display = '';
        });
    }

    // --- Intro Sequence ---

    _playIntroSequence () {
        // Advance to EXPLORE_STARTER immediately — do NOT rely on onend,
        // because speechSynthesis.cancel() in _speak suppresses onend
        // and any tap during VO-01 would permanently stall at INTRO.
        this._speak('VO-01');
        this._setPhase(PHASES.EXPLORE_STARTER, {skipReset: true});
        this._startIdleScaffolding();
    }

    // --- Code Narration (new for Activity 4) ---

    _checkCodeNarration () {
        const selectedSprite = this._getSelectedSpriteName();
        if (!selectedSprite || this._codeNarrationFired[selectedSprite]) {
            return;
        }

        const voKey = this._config.codeNarration[selectedSprite];
        if (voKey) {
            this._codeNarrationFired[selectedSprite] = true;
            this._speak(voKey);

            // Track code-reading: if Explorer is selected and viewed
            if (selectedSprite === 'Explorer') {
                this._explorerScriptViewed = true;
            }
        }
    }

    _getSelectedSpriteName () {
        const page = ScratchJr.stage && ScratchJr.stage.currentPage ? ScratchJr.stage.currentPage : null;
        if (!page || !page.currentSpriteName) {
            return null;
        }
        const spriteEl = gn(page.currentSpriteName);
        const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
        return sprite ? sprite.name || null : null;
    }

    // --- Progress Milestone Checks ---

    _checkProgressMilestones () {
        const analysis = this._analyzeAllScripts();
        let changed = false;

        // Check for tap script creation
        if (analysis.hasOnclick && !this._outcomeFlags.outcome_3) {
            this._outcomeFlags.outcome_3 = true;
            this._interactionTypes.add('tap');
            changed = true;
        }

        // Check for bump script creation
        if (analysis.hasOntouch && !this._outcomeFlags.outcome_4) {
            this._outcomeFlags.outcome_4 = true;
            this._interactionTypes.add('bump');
            changed = true;
        }

        // Check for message pair
        if (analysis.hasMessagePair && !this._outcomeFlags.outcome_5) {
            this._outcomeFlags.outcome_5 = true;
            this._interactionTypes.add('message');
            changed = true;
        }

        // Check for gotopage usage
        if (analysis.hasGotopage && !this._outcomeFlags.outcome_6) {
            this._outcomeFlags.outcome_6 = true;
            this._interactionTypes.add('page_transition');
            changed = true;
        }

        // Check for new scripts beyond starter
        if (analysis.newScriptCount > 0 && !this._outcomeFlags.outcome_7) {
            this._outcomeFlags.outcome_7 = true;
            changed = true;
        }

        // Check starter script modification
        if (analysis.starterModified && !this._outcomeFlags.outcome_2) {
            this._outcomeFlags.outcome_2 = true;
            changed = true;
        }

        // When any milestone changes, schedule a phase transition check
        // so the child is never stuck waiting for a flag tap (P0-2 + P1-4).
        if (changed) {
            this._schedulePhaseCheck(1500);
            this._schedulePersistProgress();
        }
    }

    _schedulePhaseCheck (delayMs) {
        this._clearPhaseTimeout();
        this._phaseTimeout = setTimeout(() => {
            this._evaluatePhaseTransition();
        }, delayMs);
    }

    _evaluatePhaseTransition () {
        const analysis = this._analyzeAllScripts();

        switch (this._phase) {
        case PHASES.EXPLORE_STARTER:
            if (this._starterProjectRun) {
                this._setPhase(PHASES.READ_CODE, {skipReset: true});
                this._speak('VO-02');
            }
            break;

        case PHASES.READ_CODE:
            if (this._explorerScriptViewed) {
                this._setPhase(PHASES.TASK_TAP, {skipReset: true});
                this._speak('VO-06');
            }
            break;

        case PHASES.TASK_TAP:
            if (analysis.hasOnclick) {
                this._speak(this._config.audienceFraming.firstTapInteraction || 'VO-07');
                this._phaseTimeout = setTimeout(() => {
                    this._advanceToPhase2();
                }, 3500);
            }
            break;

        case PHASES.TASK_BUMP:
            if (analysis.hasOntouch) {
                this._speak(this._config.audienceFraming.firstBumpInteraction || 'VO-09');
                this._phaseTimeout = setTimeout(() => {
                    this._setPhase(PHASES.TASK_PAGE, {skipReset: true});
                    this._persistProgress();
                }, 3500);
            }
            break;

        case PHASES.TASK_PAGE:
            if (analysis.hasGotopage) {
                this._speak(this._config.audienceFraming.firstPageTransition || 'VO-10');
                this._phaseTimeout = setTimeout(() => {
                    this._advanceToPhase3();
                }, 3500);
            }
            break;

        case PHASES.BUILD_MAP:
            if (this._checkFullPlaythroughConditions(analysis)) {
                this._advanceToCelebration();
            }
            break;
        }
    }

    // --- Phase Transitions ---

    _advanceToPhase2 () {
        this._setPhase(PHASES.PHASE_2_UNLOCK, {skipReset: true});
        this._speak('VO-08', null, {
            onend: () => {
                this._setPhase(PHASES.TASK_BUMP, {skipReset: true});
                this._persistProgress();
            }
        });
    }

    _advanceToPhase3 () {
        this._setPhase(PHASES.PHASE_3_UNLOCK, {skipReset: true});
        this._speak('VO-11', null, {
            onend: () => {
                this._setPhase(PHASES.BUILD_MAP, {skipReset: true});
                this._persistProgress();
            }
        });
    }

    _advanceToCelebration () {
        this._setPhase(PHASES.CELEBRATION, {skipReset: true});
        this._outcomeFlags.outcome_8 = true;
        this._launchCelebration();
        this._speak('VO-14', null, {
            onend: () => {
                this._clearPhaseTimeout();
                this._advanceToBonus();
            }
        });
        this._clearPhaseTimeout();
        this._phaseTimeout = setTimeout(() => {
            this._advanceToBonus();
        }, 9000);
        this._stateMachine.transition('BUILDING');
    }

    _advanceToBonus () {
        if (this._phase === PHASES.BONUS_PLAY || this._phase === PHASES.COMPLETE) {
            return;
        }
        this._setPhase(PHASES.BONUS_PLAY, {skipReset: true});
        this._showDoneButton();
        this._speak('VO-15', null, {
            onend: () => {
                // After "play your map" prompt, suggest say blocks for storytelling
                // modality (verbal expression through speech bubbles).
                this._clearPhaseTimeout();
                this._phaseTimeout = setTimeout(() => {
                    this._speak('VO-22');
                }, 5000);
            }
        });
        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _completeActivity () {
        this._completionSaved = true;
        this._setPhase(PHASES.COMPLETE, {skipReset: true});
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        this._persistProgress({
            status: 'completed',
            completedAt: new Date().toISOString()
        });
        this._showCompletionModal();
    }

    // --- Validation ---

    _validateRun () {
        this._flagTapCount++;
        this._outcomeFlags.outcome_1 = true;
        this._starterProjectRun = true;

        const analysis = this._analyzeAllScripts();

        // After a flag run, check phase transitions
        this._evaluatePhaseTransition();

        // Track bump collisions for edge case scaffolding
        if (this._phase === PHASES.TASK_BUMP || this._phase === PHASES.BUILD_MAP) {
            this._bumpCollisionCount++;
        }

        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _checkFullPlaythroughConditions (analysis) {
        // Per spec: page 1 has tap + bump scripts, gotopage exists, page 2 has content
        return analysis.hasOnclick &&
               analysis.hasOntouch &&
               analysis.hasGotopage &&
               analysis.page2HasContent;
    }

    // --- Script Analysis ---

    _analyzeAllScripts () {
        const allBlocks = this._getAllBlocksAllSprites();
        const page1Blocks = this._getBlocksForPage(0);
        const page2Blocks = this._getBlocksForPage(1);

        let hasOnclick = false;
        let hasOntouch = false;
        let hasMessage = false;
        let hasOnmessage = false;
        let hasGotopage = false;
        let messageColors = new Set();
        let onmessageColors = new Set();

        allBlocks.forEach((block) => {
            if (!block) {
                return;
            }
            if (block.blocktype === 'onclick') {
                hasOnclick = true;
            }
            if (block.blocktype === 'ontouch') {
                hasOntouch = true;
            }
            if (block.blocktype === 'message') {
                hasMessage = true;
                if (block.arg !== undefined && block.arg !== null) {
                    messageColors.add(String(block.arg));
                }
            }
            if (block.blocktype === 'onmessage') {
                hasOnmessage = true;
                if (block.arg !== undefined && block.arg !== null) {
                    onmessageColors.add(String(block.arg));
                }
            }
            if (block.blocktype === 'gotopage') {
                hasGotopage = true;
            }
        });

        // Check for matching message pair (same color)
        let hasMessagePair = false;
        if (hasMessage && hasOnmessage) {
            for (const color of messageColors) {
                if (onmessageColors.has(color)) {
                    hasMessagePair = true;
                    break;
                }
            }
        }

        // Page 2 has content if any sprites exist on page 2 with scripts
        const page2HasContent = page2Blocks.length > 0;

        // Check starter modification: did any starter script change?
        const starterModified = this._detectStarterModification();

        // Count new scripts beyond what the starter had
        const newScriptCount = this._countNewScripts();

        return {
            hasOnclick,
            hasOntouch,
            hasMessage,
            hasOnmessage,
            hasMessagePair,
            hasGotopage,
            page2HasContent,
            starterModified,
            newScriptCount,
            totalBlocks: allBlocks.length
        };
    }

    _getAllBlocksAllSprites () {
        const blocks = [];
        const pages = this._getAllPages();
        pages.forEach((page) => {
            const spriteIds = page && typeof page.getSprites === 'function' ? page.getSprites() : [];
            spriteIds.forEach((spriteId) => {
                const spriteEl = spriteId ? gn(spriteId) : null;
                const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
                const scriptsDiv = sprite ? gn(`${sprite.id}_scripts`) : null;
                if (scriptsDiv && scriptsDiv.owner && typeof scriptsDiv.owner.getBlocks === 'function') {
                    const spriteBlocks = scriptsDiv.owner.getBlocks() || [];
                    spriteBlocks.forEach((block) => {
                        if (block) {
                            blocks.push(block);
                        }
                    });
                }
            });
        });
        return blocks;
    }

    _getBlocksForPage (pageIndex) {
        const blocks = [];
        const pages = this._getAllPages();
        const page = pages[pageIndex];
        if (!page) {
            return blocks;
        }
        const spriteIds = typeof page.getSprites === 'function' ? page.getSprites() : [];
        spriteIds.forEach((spriteId) => {
            const spriteEl = spriteId ? gn(spriteId) : null;
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            const scriptsDiv = sprite ? gn(`${sprite.id}_scripts`) : null;
            if (scriptsDiv && scriptsDiv.owner && typeof scriptsDiv.owner.getBlocks === 'function') {
                const spriteBlocks = scriptsDiv.owner.getBlocks() || [];
                spriteBlocks.forEach((block) => {
                    if (block) {
                        blocks.push(block);
                    }
                });
            }
        });
        return blocks;
    }

    _getAllPages () {
        if (!ScratchJr.stage || !ScratchJr.stage.pages) {
            return ScratchJr.stage && ScratchJr.stage.currentPage ? [ScratchJr.stage.currentPage] : [];
        }
        return Array.isArray(ScratchJr.stage.pages) ? ScratchJr.stage.pages : [];
    }

    _detectStarterModification () {
        // Compare current block-type signatures against cached starter signatures.
        // Detects both count changes AND type swaps (Claude review #3).
        const page = this._getAllPages()[0];
        if (!page) {
            return false;
        }
        const spriteIds = typeof page.getSprites === 'function' ? page.getSprites() : [];
        for (const spriteId of spriteIds) {
            const spriteEl = spriteId ? gn(spriteId) : null;
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            if (!sprite || !sprite.role) {
                continue;
            }
            const expectedSig = this._starterSignatures[sprite.role];
            if (!expectedSig) {
                continue; // Skip sprites that started empty
            }
            const scriptsDiv = gn(`${sprite.id}_scripts`);
            if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
                continue;
            }
            const blocks = scriptsDiv.owner.getBlocks() || [];
            const currentSig = blocks.map((b) => b && b.blocktype ? b.blocktype : '').join(',');
            if (currentSig !== expectedSig) {
                return true;
            }
        }
        return false;
    }

    _countNewScripts () {
        // Count scripts on characters that started with no scripts (Chest, Key)
        // plus any scripts on page 2
        let count = 0;
        const page = this._getAllPages()[0];
        if (page) {
            const spriteIds = typeof page.getSprites === 'function' ? page.getSprites() : [];
            for (const spriteId of spriteIds) {
                const spriteEl = spriteId ? gn(spriteId) : null;
                const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
                if (!sprite || (sprite.role !== 'chest' && sprite.role !== 'key')) {
                    continue;
                }
                const scriptsDiv = gn(`${sprite.id}_scripts`);
                if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.gettopblocks !== 'function') {
                    continue;
                }
                const topBlocks = scriptsDiv.owner.gettopblocks() || [];
                count += topBlocks.length;
            }
        }
        // Also count page 2 scripts
        count += this._getBlocksForPage(1).length > 0 ? 1 : 0;
        return count;
    }

    // --- Idle Scaffolding ---

    _startIdleScaffolding () {
        this._clearIdleTimers();
        if (this._phase === PHASES.INTRO || this._phase === PHASES.CELEBRATION || this._phase === PHASES.COMPLETE) {
            return;
        }

        const thresholds = this._config.idleScaffolding[this._phase] || [];
        thresholds.forEach((delayMs, index) => {
            const timer = setTimeout(() => {
                this._handleIdleTier(index + 1);
            }, delayMs);
            this._idleTimers.push(timer);
        });
    }

    _handleIdleTier (tier) {
        if (this._stateMachine && this._stateMachine.state === 'RUNNING') {
            return;
        }
        if (Date.now() - this._lastInteractionAt < 800) {
            return;
        }

        if (tier === 1) {
            // Open question (lightest touch). Use phase-specific voiceId so
            // telemetry records the correct narration context, not always VO-06.
            const idleKey = `VO-IDLE-${this._phase}`;
            this._speak(idleKey, this._buildPhasePrompt(this._phase));
            return;
        }

        if (tier === 2) {
            this._highlightHintTarget();
            return;
        }

        if (tier === 3) {
            const hint = this._buildSpecificHint();
            this._highlightHintTarget(true);
            this._speak(hint.voiceId, hint.text);
            return;
        }

        // Tier 4: ghost hand
        this._showGhostHand();
    }

    _buildPhasePrompt (phase, isResume = false) {
        const prefix = isResume ? "You're back! " : '';

        switch (phase) {
        case PHASES.EXPLORE_STARTER:
            return `${prefix}Tap the green flag to see what's already here!`;
        case PHASES.READ_CODE:
            return `${prefix}Tap on Explorer to see the code that's already there.`;
        case PHASES.TASK_TAP:
            return `${prefix}What should happen when the player taps the chest?`;
        case PHASES.TASK_BUMP:
            return `${prefix}What should happen when Explorer bumps into Dragon?`;
        case PHASES.TASK_PAGE:
            return `${prefix}Can you send the player to a new page?`;
        case PHASES.BUILD_MAP:
            return `${prefix}Build the treasure room on page 2 and play through the whole map!`;
        case PHASES.BONUS_PLAY:
            return `${prefix}Play your treasure map one more time!`;
        default:
            return `${prefix}Keep building your treasure map!`;
        }
    }

    _buildSpecificHint () {
        switch (this._phase) {
        case PHASES.EXPLORE_STARTER:
            return {voiceId: 'VO-17', text: "Have you tried tapping the flag yet? It's fun to see what's already there!"};
        case PHASES.READ_CODE:
            return {voiceId: 'VO-03', text: 'Tap on Explorer to see the blocks that are already there.'};
        case PHASES.TASK_TAP:
            return {voiceId: 'VO-20', text: 'Tap on the chest. Then add a Start on Tap block and something fun!'};
        case PHASES.TASK_BUMP:
            return {voiceId: 'VO-18', text: 'Try adding a Start on Bump block to Explorer or Dragon!'};
        case PHASES.TASK_PAGE:
            return {voiceId: 'VO-08', text: 'The red page block can take the player to a new page. Try it on the chest!'};
        case PHASES.BUILD_MAP:
            return {voiceId: 'VO-13', text: "What's in the treasure room? Switch to page 2 and add some characters!"};
        default:
            return {voiceId: 'VO-06', text: 'Keep building!'};
        }
    }

    _getHintTarget () {
        switch (this._phase) {
        case PHASES.EXPLORE_STARTER:
            return {kind: 'run'};
        case PHASES.READ_CODE:
            return {kind: 'block', blockType: 'onflag', categoryIndex: 0};
        case PHASES.TASK_TAP:
            return {kind: 'block', blockType: 'onclick', categoryIndex: 0};
        case PHASES.TASK_BUMP:
            return {kind: 'block', blockType: 'ontouch', categoryIndex: 0};
        case PHASES.TASK_PAGE:
            return {kind: 'block', blockType: 'gotopage', categoryIndex: 5};
        case PHASES.BUILD_MAP:
            return {kind: 'block', blockType: 'onflag', categoryIndex: 0};
        default:
            return {kind: 'block', blockType: 'onclick', categoryIndex: 0};
        }
    }

    _highlightHintTarget (prominent = false) {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        if (!target) {
            return;
        }

        if (target.kind === 'run') {
            const runButton = this._findRunButton();
            if (runButton) {
                runButton.classList.add(prominent ? 'activity-storywalk-target' : 'activity-storywalk-pulse');
            }
            return;
        }

        this._selectPaletteCategory(target.categoryIndex);
        const block = this._findPaletteBlock(target.blockType);
        if (block) {
            block.classList.add(prominent ? 'activity-storywalk-target' : 'activity-storywalk-pulse');
        }
    }

    _showGhostHand () {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        if (!target || target.kind === 'run') {
            this._highlightHintTarget(true);
            return;
        }

        this._selectPaletteCategory(target.categoryIndex);
        const block = this._findPaletteBlock(target.blockType);
        const scripts = gn('scripts');
        if (!block || !scripts) {
            return;
        }

        const blockRect = block.getBoundingClientRect();
        const scriptRect = scripts.getBoundingClientRect();
        this._ghostHand = document.createElement('div');
        this._ghostHand.className = 'activity-storywalk-ghost-hand';
        this._ghostHand.style.left = `${blockRect.left + (blockRect.width / 2)}px`;
        this._ghostHand.style.top = `${blockRect.top + (blockRect.height / 2)}px`;
        this._ghostHand.style.setProperty('--activity-ghost-dx', `${scriptRect.left + 140 - (blockRect.left + (blockRect.width / 2))}px`);
        this._ghostHand.style.setProperty('--activity-ghost-dy', `${scriptRect.top + 58 - (blockRect.top + (blockRect.height / 2))}px`);
        document.body.appendChild(this._ghostHand);

        setTimeout(() => {
            if (this._ghostHand && this._ghostHand.parentNode) {
                this._ghostHand.parentNode.removeChild(this._ghostHand);
                this._ghostHand = null;
            }
        }, 1800);
    }

    _clearHintVisuals () {
        const root = (this._container && this._container.ownerDocument) || document;
        root.querySelectorAll('.activity-storywalk-pulse').forEach((el) => {
            el.classList.remove('activity-storywalk-pulse');
        });
        root.querySelectorAll('.activity-storywalk-target').forEach((el) => {
            el.classList.remove('activity-storywalk-target');
        });
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
            this._ghostHand = null;
        }
    }

    // --- Script Monitoring ---

    _startScriptMonitor () {
        this._stopScriptMonitor();
        this._scriptMonitor = setInterval(() => {
            this._checkProgressMilestones();
        }, 500);
    }

    _stopScriptMonitor () {
        if (this._scriptMonitor) {
            clearInterval(this._scriptMonitor);
            this._scriptMonitor = null;
        }
    }

    // --- Celebration ---

    _launchCelebration () {
        ActivityAudio.play('confetti');
        if (!this._celebrationLayer) {
            return;
        }
        this._celebrationLayer.innerHTML = '';
        // Gold sparkle particles per spec
        for (let i = 0; i < 12; i++) {
            const spark = document.createElement('span');
            spark.className = 'activity-treasuremap-sparkle';
            spark.style.animationDelay = `${i * 0.12}s`;
            spark.style.top = `${20 + Math.random() * 280}px`;
            spark.style.left = `${20 + Math.random() * 420}px`;
            this._celebrationLayer.appendChild(spark);
        }
        this._celebrationLayer.classList.add('is-active');
        setTimeout(() => {
            if (this._celebrationLayer) {
                this._celebrationLayer.classList.remove('is-active');
                this._celebrationLayer.innerHTML = '';
            }
        }, 5000);
    }

    // --- Completion Modal ---

    _showCompletionModal () {
        this._completionOverlay = document.createElement('div');
        this._completionOverlay.className = 'activity-completion-overlay';

        const modal = document.createElement('div');
        modal.className = 'activity-completion-modal';

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'activity-completion-close';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
        });
        modal.appendChild(closeBtn);

        // Header
        const header = document.createElement('div');
        header.className = 'activity-completion-header';
        const logo = document.createElement('img');
        logo.src = 'assets/lobby/scratchJrlogo.svg';
        logo.alt = 'Blooks';
        logo.className = 'activity-completion-logo';
        header.appendChild(logo);
        const title = document.createElement('h3');
        title.className = 'activity-completion-title';
        title.textContent = 'Activity Complete!';
        header.appendChild(title);
        const subtitle = document.createElement('p');
        subtitle.className = 'activity-completion-subtitle';
        subtitle.textContent = 'The Treasure Map';
        header.appendChild(subtitle);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'activity-completion-body';

        const stars = document.createElement('div');
        stars.className = 'activity-completion-stars';
        stars.textContent = '\u2B50\u2B50\u2B50';
        body.appendChild(stars);

        const message = document.createElement('p');
        message.className = 'activity-completion-message';
        message.textContent = "You started with someone else's work and made it yours! Taps, bumps, and messages make your game interactive. You built something for someone else to play!";
        body.appendChild(message);

        // Growth reflection — portfolio connection to prior activities
        const growth = this._buildGrowthSummary();
        if (growth) {
            const growthEl = document.createElement('div');
            growthEl.className = 'activity-completion-growth';
            growthEl.setAttribute('aria-label', 'Your coding journey');
            growthEl.innerHTML = growth;
            body.appendChild(growthEl);
        }

        const actions = document.createElement('div');
        actions.className = 'activity-completion-actions';

        const replayBtn = document.createElement('button');
        replayBtn.className = 'activity-completion-btn-replay';
        replayBtn.setAttribute('aria-label', 'Replay activity');
        replayBtn.innerHTML = '&#8634; Replay';
        replayBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            this.restartActivity();
        });
        actions.appendChild(replayBtn);

        const nextBtn = document.createElement('button');
        nextBtn.className = 'activity-completion-btn-next';
        nextBtn.setAttribute('aria-label', 'Next activity');
        nextBtn.innerHTML = 'Next Activity &#9654;';
        nextBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            ScratchJr.saveProject(null, ScratchJr.flippage);
        });
        actions.appendChild(nextBtn);

        body.appendChild(actions);
        modal.appendChild(body);
        this._completionOverlay.appendChild(modal);
        document.body.appendChild(this._completionOverlay);
    }

    _buildGrowthSummary () {
        // Read prior activity progress to build a portfolio growth reflection.
        let progress = {};
        try {
            progress = JSON.parse(localStorage.getItem('scratchjr_activity_progress') || '{}');
        } catch (_) {
            return null;
        }

        const completed = ['slide-right-cat', 'bug-band-concert', 'bluux-moonwalk', 'treasure-map']
            .filter((id) => progress[id] && progress[id].status === 'completed');

        const skills = [];
        if (progress['slide-right-cat']) {
            skills.push('sequences');
        }
        if (progress['bug-band-concert']) {
            skills.push('parallelism', 'debugging');
        }
        if (progress['bluux-moonwalk']) {
            skills.push('loops');
        }
        skills.push('conditionals', 'remixing');

        const interactionCount = this._interactionTypes.size;
        const lines = [];
        lines.push(`<strong>Your coding journey: ${completed.length} of 5 activities!</strong>`);
        lines.push(`Skills learned: ${skills.join(', ')}`);
        if (interactionCount > 1) {
            lines.push(`You built ${interactionCount} types of interactions in this map!`);
        }
        if (this._outcomeFlags.outcome_2) {
            lines.push('You read someone else\'s code and made it your own!');
        }
        return lines.join('<br>');
    }

    _dismissCompletionModal () {
        if (this._completionOverlay) {
            this._completionOverlay.style.opacity = '0';
            setTimeout(() => {
                if (this._completionOverlay && this._completionOverlay.parentNode) {
                    this._completionOverlay.parentNode.removeChild(this._completionOverlay);
                }
                this._completionOverlay = null;
            }, 300);
        }
    }

    // --- Done Button ---

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }

        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-storywalk-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            this._speak('VO-16', null, {
                onend: () => this._completeActivity()
            });
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    // --- Voice / TTS ---

    _replayCurrentNarration () {
        if (this._lastNarrationId && this._lastNarrationText) {
            this._speak(this._lastNarrationId, this._lastNarrationText);
        }
    }

    _initVoiceSelection () {
        if (!window.speechSynthesis) {
            return;
        }
        this._refreshPreferredVoice();
        if (!this._speechVoice && typeof window.speechSynthesis.addEventListener === 'function') {
            window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _waitForPreferredVoice (timeoutMs = 5000) {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return Promise.resolve();
        }

        this._refreshPreferredVoice();
        if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let finished = false;
            let pollTimer = null;
            let timeoutId = null;

            const cleanup = () => {
                if (pollTimer) {
                    clearInterval(pollTimer);
                    pollTimer = null;
                }
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                if (typeof window.speechSynthesis.removeEventListener === 'function') {
                    window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
                }
            };

            const finish = () => {
                if (finished) {
                    return;
                }
                finished = true;
                cleanup();
                this._refreshPreferredVoice();
                resolve();
            };

            const handleVoicesChanged = () => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    finish();
                }
            };

            if (typeof window.speechSynthesis.addEventListener === 'function') {
                window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
            }

            pollTimer = setInterval(() => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    finish();
                }
            }, 100);

            timeoutId = setTimeout(finish, timeoutMs);
        });
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
        this._speechVoiceScore = selection ? selection.score : 0;
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }

        const voices = window.speechSynthesis.getVoices();
        if (!Array.isArray(voices) || voices.length < 1) {
            return null;
        }

        const ranked = voices
            .map((voice) => ({voice, score: this._scoreVoice(voice)}))
            .sort((a, b) => b.score - a.score);

        return ranked[0] && ranked[0].score > 0 ? ranked[0] : null;
    }

    _scoreVoice (voice) {
        const name = String(voice && voice.name || '').toLowerCase();
        const lang = String(voice && voice.lang || '').toLowerCase();
        const uri = String(voice && voice.voiceURI || '').toLowerCase();
        const haystack = `${name} ${lang} ${uri}`;

        let score = 0;

        if (lang.startsWith('en-us')) {
            score += 60;
        } else if (lang.startsWith('en')) {
            score += 45;
        }

        if (voice && voice.default) {
            score += 10;
        }

        [
            'aria', 'jenny', 'zira', 'samantha', 'ava', 'michelle',
            'emma', 'libby', 'sonia', 'natasha', 'sara', 'susan',
            'female', 'woman', 'girl', 'google us english',
            'google uk english female'
        ].forEach((token) => {
            if (haystack.includes(token)) {
                score += 40;
            }
        });

        ['natural', 'neural', 'online'].forEach((token) => {
            if (haystack.includes(token)) {
                score += 18;
            }
        });

        [
            'david', 'guy', 'mark', 'matthew', 'male',
            'man', 'boy', 'ryan'
        ].forEach((token) => {
            if (haystack.includes(token)) {
                score -= 30;
            }
        });

        return score;
    }

    _speak (voiceId, fallbackText, opts = {}) {
        const text = fallbackText || this._config.voiceOver[voiceId];
        if (!text) {
            if (opts.onend) {
                opts.onend();
            }
            return;
        }

        this._lastNarrationId = voiceId;
        this._lastNarrationText = text;
        this._lastInteractionAt = Date.now();

        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            if (opts.onend) {
                opts.onend();
            }
            return;
        }

        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(text);
            const preferredVoice = this._getPreferredVoice();
            utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
            if (preferredVoice) {
                utterance.voice = preferredVoice;
            }
            utterance.rate = 0.85;
            utterance.pitch = 1.05;
            utterance.volume = 1;
            utterance.onend = () => {
                if (opts.onend) {
                    opts.onend();
                }
            };
            window.speechSynthesis.speak(utterance);
        } catch (_) {
            if (opts.onend) {
                opts.onend();
            }
        }
    }

    // --- Persistence ---

    _persistProgress (overrides = {}) {
        this._clearPersistTimer();
        const now = Date.now();
        const elapsedMs = Math.max(0, now - this._sessionStartedAt - this._idleTimeMs);
        const status = overrides.status || (this._completionSaved ? 'completed' : 'in_progress');

        return ActivityStorageService.saveProgress(this._activityData.activityId, {
            status,
            attempts: 0,
            stars: status === 'completed' ? 3 : 0,
            bestScore: status === 'completed' ? 3 : 0,
            hintsUsed: 0,
            timeSpentMs: elapsedMs,
            currentState: overrides.currentState || this._phase,
            totalActiveTimeSec: Math.round(elapsedMs / 1000),
            flagTapCount: this._flagTapCount,
            interactionTypes: Array.from(this._interactionTypes),
            starterProjectRun: this._starterProjectRun,
            explorerScriptViewed: this._explorerScriptViewed,
            codeNarrationFired: {...this._codeNarrationFired},
            bumpCollisionCount: this._bumpCollisionCount,
            outcomeFlags: {...this._outcomeFlags},
            completedAt: overrides.completedAt,
            lastOpenedAt: new Date().toISOString(),
            sessionStartedAt: this._sessionStartedAt,
            idleTimeMs: this._idleTimeMs
        });
    }

    _schedulePersistProgress (overrides = {}, delayMs = 250) {
        this._clearPersistTimer();
        this._persistTimer = setTimeout(() => {
            this._persistTimer = null;
            this._persistProgress(overrides);
        }, delayMs);
    }

    _hydrateFromProgress (saved) {
        this._sessionStartedAt = typeof saved.sessionStartedAt === 'number' ? saved.sessionStartedAt : Date.now();
        this._idleTimeMs = typeof saved.idleTimeMs === 'number' ? saved.idleTimeMs : 0;
        this._flagTapCount = Number(saved.flagTapCount || 0);
        this._starterProjectRun = Boolean(saved.starterProjectRun);
        this._explorerScriptViewed = Boolean(saved.explorerScriptViewed);
        this._bumpCollisionCount = Number(saved.bumpCollisionCount || 0);
        if (saved.codeNarrationFired && typeof saved.codeNarrationFired === 'object') {
            this._codeNarrationFired = {...saved.codeNarrationFired};
        }
        if (Array.isArray(saved.interactionTypes)) {
            this._interactionTypes = new Set(saved.interactionTypes);
        }
        if (saved.outcomeFlags && typeof saved.outcomeFlags === 'object') {
            this._outcomeFlags = {...this._outcomeFlags, ...saved.outcomeFlags};
        }
        this._completionSaved = saved.status === 'completed';
    }

    _getResumePhase (savedState) {
        if (!savedState || !PHASES[savedState]) {
            return null;
        }
        const rank = PHASE_RANKS[savedState];
        if (rank === undefined || rank < PHASE_RANKS.EXPLORE_STARTER) {
            return null;
        }
        // Don't resume into transient phases
        if (savedState === PHASES.PHASE_2_UNLOCK || savedState === PHASES.PHASE_3_UNLOCK) {
            return this._normalizeResumePhase(savedState);
        }
        if (savedState === PHASES.CELEBRATION) {
            return PHASES.BONUS_PLAY;
        }
        return savedState;
    }

    _normalizeResumePhase (state) {
        if (state === PHASES.PHASE_2_UNLOCK) {
            return PHASES.TASK_BUMP;
        }
        if (state === PHASES.PHASE_3_UNLOCK) {
            return PHASES.BUILD_MAP;
        }
        return state;
    }

    // --- DOM Helpers ---

    _selectPaletteCategory (index) {
        if (typeof index !== 'number') {
            return;
        }
        try {
            Palette.selectCategory(index);
        } catch (_) {}
    }

    _findPaletteBlock (blockType) {
        const palette = document.getElementById('palette');
        if (!palette) {
            return null;
        }
        return Array.from(palette.childNodes || []).find((child) =>
            child && child.owner && child.owner.blocktype === blockType
        ) || null;
    }

    _findRunButton () {
        return document.getElementById('go');
    }

    // --- Timer Cleanup ---

    _clearIdleTimers () {
        while (this._idleTimers.length > 0) {
            clearTimeout(this._idleTimers.pop());
        }
        if (!this._idlePausedAt) {
            this._idlePausedAt = Date.now();
        }
    }

    _clearPersistTimer () {
        if (this._persistTimer) {
            clearTimeout(this._persistTimer);
            this._persistTimer = null;
        }
    }

    _clearPhaseTimeout () {
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
    }

    static _isThreadInsideForeverLoop (thread) {
        let block = thread && thread.thisblock;
        while (block) {
            if (block.blocktype === 'forever') {
                return true;
            }
            block = block.prev;
        }
        return false;
    }

    _isStarterAmbienceThread (thread) {
        const spriteName = thread && thread.spr && thread.spr.name ? String(thread.spr.name) : '';
        if (!spriteName) {
            return false;
        }

        const signature = TreasureMapFlow._serializeRuntimeBlockChain(thread.firstBlock);
        return TreasureMapFlow._isThreadInsideForeverLoop(thread) &&
            this._starterAmbienceSignatures.has(`${spriteName}:${signature}`);
    }

    static _collectStarterAmbienceSignatures (activityData) {
        const signatures = new Set();
        const pages = activityData && activityData.projectTemplate &&
            Array.isArray(activityData.projectTemplate.pages)
            ? activityData.projectTemplate.pages : [];

        pages.forEach((page) => {
            const sprites = page && Array.isArray(page.sprites) ? page.sprites : [];
            sprites.forEach((sprite) => {
                if (!sprite || !sprite.name || !Array.isArray(sprite.scripts)) {
                    return;
                }

                sprite.scripts.forEach((script) => {
                    if (TreasureMapFlow._templateScriptContainsForever(script)) {
                        signatures.add(`${sprite.name}:${TreasureMapFlow._serializeTemplateBlockList(script)}`);
                    }
                });
            });
        });

        return signatures;
    }

    static _templateScriptContainsForever (blocks) {
        if (!Array.isArray(blocks)) {
            return false;
        }

        return blocks.some((block) => block &&
            (block.blocktype === 'forever' || TreasureMapFlow._templateScriptContainsForever(block.inside)));
    }

    static _serializeTemplateBlockList (blocks) {
        if (!Array.isArray(blocks)) {
            return '';
        }

        const parts = [];
        blocks.forEach((block) => {
            if (!block) {
                return;
            }

            parts.push(TreasureMapFlow._serializeTemplateBlock(block));
            if (Array.isArray(block.inside) && block.inside.length > 0) {
                const nested = TreasureMapFlow._serializeTemplateBlockList(block.inside);
                if (nested) {
                    parts.push(nested);
                }
            }
        });

        return parts.join('>');
    }

    static _serializeTemplateBlock (block) {
        if (!block) {
            return '';
        }

        const arg = block.arg == null ? '' : String(block.arg);
        return `${block.blocktype}:${arg}`;
    }

    static _serializeRuntimeBlockChain (block) {
        const parts = [];
        let current = block;
        while (current) {
            parts.push(TreasureMapFlow._serializeRuntimeBlock(current));
            if (current.inside) {
                const nested = TreasureMapFlow._serializeRuntimeBlockChain(current.inside);
                if (nested) {
                    parts.push(nested);
                }
            }
            current = current.next;
        }
        return parts.join('>');
    }

    static _serializeRuntimeBlock (block) {
        if (!block) {
            return '';
        }

        const arg = TreasureMapFlow._getRuntimeBlockArg(block);
        return `${block.blocktype}:${arg}`;
    }

    static _getRuntimeBlockArg (block) {
        if (!block) {
            return '';
        }

        if (typeof block.getArgValue === 'function') {
            const value = block.getArgValue();
            return value == null ? '' : String(value);
        }

        if (block.arg && typeof block.arg.argValue !== 'undefined') {
            return block.arg.argValue == null ? '' : String(block.arg.argValue);
        }

        return '';
    }
}

export {PHASES};
